import { useRef, useLayoutEffect, useState } from 'react';
import GlossaryMarkdown from '~/widgets/GlossaryMarkdown/GlossaryMarkdown';
import type { GlossaryEntry } from '~/generated/glossary.types';
import styles from '~/widgets/GlossaryTooltip/GlossaryTooltip.module.scss';

export interface GlossaryTooltipProps {
    entry: GlossaryEntry;
    position: { top: number; left: number };
    onClose?: () => void;
}

/**
 * Tooltip that displays glossary entry details on hover/click.
 * Shows definition, formula expression, and references.
 */
export default function GlossaryTooltip({ entry, position, onClose: _onClose }: GlossaryTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Adjust position to keep tooltip within viewport
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const parentRect = tooltip.parentElement!.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let { top, left } = position;

    // Adjust horizontal position if tooltip goes off right edge
    if (left + rect.width > viewportWidth - 16) {
      left = viewportWidth - rect.width - 16;
    }

    // Keep tooltip from going off left edge
    if (left < 16) {
      left = 16;
    }

    // Adjust vertical position if tooltip goes off bottom edge
    if (top + rect.height > viewportHeight - 16) {
      // Try to position above the element instead
      top = position.top - (rect.height + parentRect.height + 32);
    }

    // Keep tooltip from going off top edge
    if (top < 16) {
      top = 16;
    }

    setAdjustedPosition({ top, left });
  }, [position]);

  return (
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      style={{
        position: 'fixed',
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
      }}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h4>{entry.name[0]}</h4>
          <div className={styles.meta}>
            <span className={styles.type}>{entry.type}</span>
            <span className={styles.category}>{entry.category}</span>
          </div>
        </div>

        <GlossaryMarkdown className={styles.body} enableTooltips={false}>
          {entry.content}
        </GlossaryMarkdown>

        {entry.expression && (
          <div className={styles.formula}>
            <code>{entry.expression}</code>
          </div>
        )}

        {(entry.referencedBy.length > 0) && (
          <div className={styles.refs}>
            {/*{entry.refs.length > 0 && (
                            <div className={styles.refGroup}>
                                <small>References: {entry.refs.join(', ')}</small>
                            </div>
                        )}*/}
            {entry.referencedBy.length > 0 && (
              <div className={styles.refGroup}>
                <small>See also: {entry.referencedBy.join(', ')}</small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
